/**
 * Feature 100 — trial controls in the admin advisor editor.
 * Start (with day counter, default 7), extend (also reactivates an expired
 * trial), convert to paid. Action-based: each button calls its endpoint
 * directly and reports the fresh trial state upward — deliberately outside the
 * PATCH form so trial state can't be accidentally edited via profile saves.
 */

import { useState } from 'react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/components/Toast';
import { AdvisorEditorSection } from './AdvisorEditorSection';
import {
  startAdvisorTrial,
  extendAdvisorTrial,
  convertAdvisorTrial,
  endAdvisorTrial,
  type AdvisorTrialState,
} from '../../services/adminAdvisorsApi';

type AdvisorTrialSectionProps = {
  advisorId: string;
  initialTrial: Pick<AdvisorTrialState, 'isTrial' | 'trialExpiresAt' | 'trialConvertedAt'>;
};

/** null when there is no expiry or the stored value doesn't parse. */
const daysRemaining = (expiresAt: string | null): number | null => {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt).getTime();
  if (Number.isNaN(expiry)) return null;
  return Math.ceil((expiry - Date.now()) / (24 * 60 * 60 * 1000));
};

export const AdvisorTrialSection = ({ advisorId, initialTrial }: AdvisorTrialSectionProps) => {
  const { showToast } = useToast();
  const [trial, setTrial] =
    useState<Pick<AdvisorTrialState, 'isTrial' | 'trialExpiresAt' | 'trialConvertedAt'>>(
      initialTrial,
    );
  const [days, setDays] = useState('7');
  const [busy, setBusy] = useState(false);

  const remaining = daysRemaining(trial.trialExpiresAt);
  const isExpired = trial.isTrial && remaining !== null && remaining <= 0;

  const run = async (action: () => Promise<AdvisorTrialState>, successMessage: string) => {
    setBusy(true);
    try {
      const state = await action();
      setTrial(state);
      showToast(successMessage, 'success');
    } catch (error) {
      const message =
        (error as { message?: string })?.message ?? 'Trial action failed. Please try again.';
      showToast(message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const dayCount = Number.parseInt(days, 10);
  const daysValid = Number.isInteger(dayCount) && dayCount >= 1 && dayCount <= 365;

  return (
    <AdvisorEditorSection title="Trial (Internal)">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600">Status:</span>
        {trial.isTrial ? (
          isExpired ? (
            <Badge variant="destructive">Trial expired</Badge>
          ) : remaining === null ? (
            <Badge variant="secondary">Trial — expiry unknown</Badge>
          ) : (
            <Badge variant="secondary">Trial — {remaining} day{remaining === 1 ? '' : 's'} left</Badge>
          )
        ) : trial.trialConvertedAt ? (
          <Badge variant="secondary">Converted from trial</Badge>
        ) : (
          <Badge variant="outline">Not a trial</Badge>
        )}
        {trial.isTrial && trial.trialExpiresAt && remaining !== null ? (
          <span className="text-xs text-gray-500">
            {isExpired ? 'Expired' : 'Expires'} {new Date(trial.trialExpiresAt).toLocaleDateString()}
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-28">
          <Label htmlFor="trial-days">Days</Label>
          <Input
            id="trial-days"
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
        </div>

        {!trial.isTrial ? (
          <Button
            type="button"
            disabled={busy || !daysValid}
            onClick={() => run(() => startAdvisorTrial(advisorId, dayCount), 'Trial started')}
          >
            Start Trial
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              disabled={busy || !daysValid}
              onClick={() =>
                run(
                  () => extendAdvisorTrial(advisorId, dayCount),
                  isExpired ? 'Trial reactivated' : 'Trial extended',
                )
              }
            >
              {isExpired ? 'Reactivate (+days)' : 'Extend Trial'}
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => run(() => convertAdvisorTrial(advisorId), 'Trial converted to paid')}
            >
              Convert to Paid
            </Button>
            {!isExpired && (
              <Button
                type="button"
                variant="destructive"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      'End this trial now? The advisor loses access immediately and receives the trial-ended email. You can reactivate or convert later.',
                    )
                  ) {
                    void run(() => endAdvisorTrial(advisorId), 'Trial ended');
                  }
                }}
              >
                End Trial Now
              </Button>
            )}
          </>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Trials get full FP capability and look identical to paid accounts outside this portal.
        Converting keeps everything the advisor built and starts the regular onboarding emails.
      </p>
    </AdvisorEditorSection>
  );
};
